import { AppSelect } from '../../ui/AppSelect';
import React, { useState, useEffect, useMemo, useRef } from "react";
import ReactDOM from "react-dom";
import { X, Sparkles, LayoutGrid, Loader2, User, MapPin, Box, CheckCircle2, Wand2 } from "lucide-react";
import { authenticatedFetch } from "../../../utils/api";
import { API_HOST } from "../../../utils/constants";
import { extractTaskResultUrl, submitTask, startTaskPolling } from "../../../services/taskService";
import MultiSelect from "./MultiSelect";
import { STYLE_OPTIONS } from "./Constants";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { uploadBase64ToCos } from "../../../utils/fileUtils";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import {
  assertWanxImageInputLimit,
  getCurrentImageProvider,
  isWanxImageProvider,
  normalizeWanxImageQualityTier,
  WANX_IMAGE_MAX_INPUT_IMAGES,
} from "../../../utils/wanxImageCapabilities";

const GridSequenceWizard = ({
  isOpen,
  onClose,
  onUpdateShots,
  existingShots = [],
  currentRatio = "16:9",
  currentStyle = "universal",
  characterOptions = [],
  sceneOptions = [],
  propOptions = []
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const COLS_PER_ROW = 5;
  const [gridCols, setGridCols] = useState(COLS_PER_ROW);
  const [gridRows, setGridRows] = useState(1);

  const [selectedCharIds, setSelectedCharIds] = useState([]);
  const [selectedSceneIds, setSelectedSceneIds] = useState([]);
  const [selectedPropIds, setSelectedPropIds] = useState([]);
  const [selectedCharVariantIndexes, setSelectedCharVariantIndexes] = useState([]);
  const [selectedSceneVariantIndexes, setSelectedSceneVariantIndexes] = useState([]);
  const [selectedPropVariantIndexes, setSelectedPropVariantIndexes] = useState([]);
  
  const [panelPrompts, setPanelPrompts] = useState({});
  const [panelSourceMap, setPanelSourceMap] = useState({});
  
  const [gridImageUrl, setGridImageUrl] = useState(null);
  const [splitImages, setSplitImages] = useState([]);
  const [isSplitting, setIsSplitting] = useState(false); 
  const openSessionInitializedRef = useRef(false);
  const imageProvider = getCurrentImageProvider();
  const isWanxImage = isWanxImageProvider(imageProvider);
  const selectedReferenceCount = selectedCharIds.length + selectedSceneIds.length + selectedPropIds.length;

  const warnWanxSelectionLimit = () => {
    toast.warning(`千问图像3.0最多支持${WANX_IMAGE_MAX_INPUT_IMAGES}张参考图，请先移除其他参考图`);
  };

  useEffect(() => {
    if (!isOpen) {
      openSessionInitializedRef.current = false;
      return;
    }
    if (!openSessionInitializedRef.current && existingShots.length > 0) {
        openSessionInitializedRef.current = true;
        const rows = Math.ceil(existingShots.length / gridCols);
        setGridRows(Math.max(1, rows));

        const initialSourceMap = {};
        const initialPrompts = {};
        
        existingShots.forEach((shot, index) => {
            const panelId = index + 1;
            initialSourceMap[panelId] = shot.id;
            initialPrompts[panelId] = shot.image_prompt || shot.visual_content || "Cinematic shot";
        });

        console.log("Wizard Map Initialized:", initialSourceMap);
        setPanelSourceMap(initialSourceMap);
        setPanelPrompts(initialPrompts);
        
        setStep(1);
        setSplitImages([]);
        setGridImageUrl(null);
    }
  }, [isOpen, existingShots, gridCols]); 

  const handleGenerateGrid = async () => {
    if (isWanxImage && selectedReferenceCount > WANX_IMAGE_MAX_INPUT_IMAGES) {
      warnWanxSelectionLimit();
      return;
    }
    setLoading(true);
    setLoadingText("正在构建宫格分镜...");
    try {
      // 构建资产引用列表，支持变体
      const characterRefs = selectedCharIds.map((id, index) => {
        const char = characterOptions.find(c => c.id === id);
        if (!char?.imageUrl) return null;

        const variantIndex = selectedCharVariantIndexes[index] ?? -1;
        let imageUrl = char.imageUrl;
        let variantName = '';

        if (variantIndex >= 0 && char.variations?.[variantIndex]) {
          imageUrl = char.variations[variantIndex].imageUrl;
          variantName = ` (${char.variations[variantIndex].title})`;
        }

        return { name: `${char.name}${variantName}`, base64: imageUrl };
      }).filter(Boolean);

      const sceneRefs = selectedSceneIds.map((id, index) => {
        const scene = sceneOptions.find(s => s.id === id);
        if (!scene?.imageUrl) return null;

        const variantIndex = selectedSceneVariantIndexes[index] ?? -1;
        let imageUrl = scene.imageUrl;
        let variantName = '';

        if (variantIndex >= 0 && scene.variations?.[variantIndex]) {
          imageUrl = scene.variations[variantIndex].imageUrl;
          variantName = ` (${scene.variations[variantIndex].title})`;
        }

        return { name: `${scene.name}${variantName}`, base64: imageUrl };
      }).filter(Boolean);

      const propRefs = selectedPropIds.map((id, index) => {
        const prop = propOptions.find(p => p.id === id);
        if (!prop?.imageUrl) return null;

        const variantIndex = selectedPropVariantIndexes[index] ?? -1;
        let imageUrl = prop.imageUrl;
        let variantName = '';

        if (variantIndex >= 0 && prop.variations?.[variantIndex]) {
          imageUrl = prop.variations[variantIndex].imageUrl;
          variantName = ` (${prop.variations[variantIndex].title})`;
        }

        return { name: `${prop.name}${variantName}`, base64: imageUrl };
      }).filter(Boolean);

      assertWanxImageInputLimit(
        [...characterRefs, ...sceneRefs, ...propRefs].map((ref) => ref.base64),
        imageProvider,
      );

      const totalCells = gridRows * gridCols;
      const promptsList = [];

      for (let i = 0; i < totalCells; i++) {
          const panelId = i + 1;
          const shotId = panelSourceMap[panelId];
          if (shotId) {
              const shot = existingShots.find(s => s.id === shotId);
              let p = panelPrompts[panelId] || (shot ? (shot.image_prompt || shot.visual_content) : "Cinematic shot");
              promptsList.push(p);
          } else {
              promptsList.push("EMPTY");
          }
      }

      const imageQualityTier = normalizeWanxImageQualityTier("4K", imageProvider);

      const { taskId } = await submitTask("GENERATE_INDUSTRIAL_GRID", {
          prompts: promptsList,
          rows: gridRows,
          cols: gridCols,
          aspectRatio: currentRatio,
          characterRefs,
          sceneRefs,
          propRefs,
          provider: imageProvider,
          imageQualityTier,
      }, {
          styleAnchor: currentStyle,
          provider: imageProvider,
      });

      setLoadingText("AI 正在生成宫格图...");

      await new Promise((resolve, reject) => {
        startTaskPolling(taskId, {
          onSuccess: async (result) => {
            try {
              const generatedUrl = extractTaskResultUrl(result);
              if (!generatedUrl) throw new Error("宫格任务完成但没有返回图片地址");
              setGridImageUrl(generatedUrl);
              setStep(2);
              resolve();
            } catch (e) {
              reject(e);
            }
          },
          onError: (errorMessage) => {
            reject(new Error(errorMessage));
          },
        });
      });

    } catch (e) {
        console.error(e);
        toast.error("出错: " + e.message);
    } finally {
        setLoading(false);
    }
  };

  const handleSplitGrid = async () => {
    if (!gridImageUrl) return;
    setIsSplitting(true);
    try {
      const splitData = await authenticatedFetch(`${API_HOST}/api/video/split-image-only`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ gridImageUrl, rows: gridRows, cols: gridCols })
      });
      const initialSplits = splitData.images.map((url, idx) => ({
        base64: url,
        panelId: idx + 1,
        isUpscaled: false,
        loading: false
      }));
      setSplitImages(initialSplits);
      toast.success("裁剪完成！");
    } catch (e) {
      toast.error("裁剪失败: " + e.message);
    } finally {
      setIsSplitting(false);
    }
  };

  const handleUpscaleAll = async () => {
      const targets = splitImages.filter(img => panelSourceMap[img.panelId]);
      if (targets.length === 0) return;
      for (const img of targets) { await handleUpscaleSingle(img); }
  };

  const handleUpscaleSingle = async (imgItem) => {
      const idx = splitImages.findIndex(i => i.panelId === imgItem.panelId);
      if (idx === -1) return;
      const newSplits = [...splitImages];
      newSplits[idx] = { ...newSplits[idx], loading: true };
      setSplitImages(newSplits);

      const currentImageProvider = getCurrentImageProvider();
      const imageQualityTier = normalizeWanxImageQualityTier("4K", currentImageProvider);

      try {
          const prompt = panelPrompts[imgItem.panelId] || "high quality";
          const { taskId } = await submitTask("UPSCALE_SLICE", {
              image: imgItem.base64,
              prompt,
              imageQualityTier,
          }, {
              styleAnchor: currentStyle,
              provider: currentImageProvider,
          });

          startTaskPolling(taskId, {
            onSuccess: (result) => {
              const hdUrl = extractTaskResultUrl(result);
              if (hdUrl) {
                setSplitImages(prev => {
                    const curr = [...prev];
                    curr[idx] = { ...curr[idx], base64: hdUrl, isUpscaled: true, loading: false };
                    return curr;
                });
              } else {
                setSplitImages(prev => { const curr = [...prev]; curr[idx] = { ...curr[idx], loading: false }; return curr; });
              }
            },
            onError: () => {
              setSplitImages(prev => { const curr = [...prev]; curr[idx] = { ...curr[idx], loading: false }; return curr; });
            },
          });
      } catch (e) {
          setSplitImages(prev => { const curr = [...prev]; curr[idx] = { ...curr[idx], loading: false }; return curr; });
      }
  };

  const handleConfirmUpdate = async () => {
    console.log("回填")
    const updates = [];
    // 并行上传所有 base64 到 COS
    const uploadPromises = splitImages.map(async (item) => {
        const targetShotId = panelSourceMap[item.panelId];
        if (!targetShotId || !item.base64) return;
        try {
            const url = await uploadBase64ToCos(item.base64);
            updates.push({
                id: targetShotId,
                sketch_base64: url,
                sketch_preview: url,
                image_prompt: panelPrompts[item.panelId],
                status: "done"
            });
        } catch (e) {
            console.error("上传草图失败:", e);
        }
    });
    await Promise.all(uploadPromises);
    if (updates.length === 0) return toast.warning("无有效内容回填");
    onUpdateShots(updates, 'merge');
    onClose();
  };

  const getCellAspectRatio = () => {
      const [w, h] = currentRatio.split(':').map(Number);
      return w / h;
  };

  const handleLinkShot = (panelId, shotIdStr) => {
      const newMap = { ...panelSourceMap };
      if (!shotIdStr) { 
          delete newMap[panelId]; 
      } else { 
          // ✅ 修复：通过查找原对象获取正确的 ID 类型（避免 string/number 不匹配）
          const targetShot = existingShots.find(s => String(s.id) === String(shotIdStr));
          if (targetShot) {
              newMap[panelId] = targetShot.id; 
          }
      }
      setPanelSourceMap(newMap);
  };

  const { mounted, exiting } = useDelayedUnmount(isOpen, 150);
  if (!mounted) return null;

  return ReactDOM.createPortal(
    <div className={`fixed inset-0 z-[9999] backdrop-blur-md flex items-center justify-center ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-300"}`} style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
      <div className={`w-[95vw] h-[90vh] rounded-3xl flex flex-col shadow-2xl overflow-hidden relative ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`} style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>

        <div className="px-8 py-5 border-b flex justify-between items-center" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-secondary)' }}>
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg" style={{ backgroundColor: `${theme.colors.bluePrimary}20` }}><LayoutGrid style={{ color: theme.colors.bluePrimary }} size={24} /></div>
            <div>
                <h3 className="text-xl font-bold tracking-tight" style={{ color: 'var(--color-text-primary)' }}>全集工业化分镜 (Industrial Grid)</h3>
                <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    Auto-Fit: <span className="font-bold" style={{ color: theme.colors.bluePrimary }}>{gridCols} x {gridRows}</span> | AR: {currentRatio}
                </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-full transition-all duration-200" style={{ color: 'var(--color-text-muted)' }}><X size={24}/></button>
        </div>

        <div className="flex-1 overflow-hidden flex">
          {/* 左侧配置 */}
          {step === 1 && (
              <div className="w-80 border-r p-6 flex flex-col gap-6 overflow-y-auto custom-scrollbar" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
                  <div className="space-y-4">
                      <label className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-text-muted)' }}>全局约束</label>
                      <MultiSelect
                        label="统一角色"
                        icon={User}
                        options={characterOptions}
                        selectedIds={selectedCharIds}
                        onChange={setSelectedCharIds}
                        variantIndexes={selectedCharVariantIndexes}
                        onVariantChange={setSelectedCharVariantIndexes}
                        maxSelection={isWanxImage ? Math.max(0, WANX_IMAGE_MAX_INPUT_IMAGES - selectedSceneIds.length - selectedPropIds.length) : undefined}
                        onSelectionLimit={warnWanxSelectionLimit}
                      />
                      <MultiSelect
                        label="统一场景"
                        icon={MapPin}
                        options={sceneOptions}
                        selectedIds={selectedSceneIds}
                        onChange={setSelectedSceneIds}
                        variantIndexes={selectedSceneVariantIndexes}
                        onVariantChange={setSelectedSceneVariantIndexes}
                        maxSelection={isWanxImage ? Math.max(0, WANX_IMAGE_MAX_INPUT_IMAGES - selectedCharIds.length - selectedPropIds.length) : undefined}
                        onSelectionLimit={warnWanxSelectionLimit}
                      />
                      <MultiSelect
                        label="统一道具"
                        icon={Box}
                        options={propOptions}
                        selectedIds={selectedPropIds}
                        onChange={setSelectedPropIds}
                        variantIndexes={selectedPropVariantIndexes}
                        onVariantChange={setSelectedPropVariantIndexes}
                        maxSelection={isWanxImage ? Math.max(0, WANX_IMAGE_MAX_INPUT_IMAGES - selectedCharIds.length - selectedSceneIds.length) : undefined}
                        onSelectionLimit={warnWanxSelectionLimit}
                      />
                      {isWanxImage && (
                        <p className="text-2xs leading-5" style={{ color: selectedReferenceCount > WANX_IMAGE_MAX_INPUT_IMAGES ? 'var(--color-error)' : 'var(--color-text-muted)' }}>
                          千问图像3.0共可选 {WANX_IMAGE_MAX_INPUT_IMAGES} 张参考图，已选 {selectedReferenceCount} 张；输出按2K生成。
                        </p>
                      )}
                  </div>
                  <div className="mt-auto">
                      <button onClick={handleGenerateGrid} disabled={loading} className="w-full py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg" style={{ background: `linear-gradient(to right, ${theme.colors.bluePrimary}, ${theme.colors.accentPrimary})`, color: 'white' }}>
                          {loading ? <Loader2 className="animate-spin" size={20}/> : <Sparkles size={20}/>}
                          {loading ? loadingText : "生成排版图"}
                      </button>
                  </div>
              </div>
          )}

          {/* 右侧预览区 */}
          <div className="flex-1 p-8 overflow-y-auto custom-scrollbar flex flex-col items-center" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
             {step === 1 ? (
                 <div className="w-full max-w-5xl grid gap-4 opacity-50" style={{ gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))` }}>
                     {Array.from({ length: gridRows * gridCols }).map((_, i) => {
                         const pId = i + 1;
                         const shotId = panelSourceMap[pId];
                         const shot = existingShots.find(s => s.id === shotId);
                         return (
                             <div key={i} className="rounded-lg flex flex-col items-center justify-center relative overflow-hidden group" style={{ aspectRatio: getCellAspectRatio(), backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                                 <span className="absolute top-2 left-2 text-2xs font-mono" style={{ color: 'var(--color-text-muted)' }}>P-{pId}</span>
                                 <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                    <AppSelect
                                        className="rounded px-1 py-0.5 max-w-[80px] text-2xs"
                                        style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                                        value={panelSourceMap[pId] || ""}
                                        onChange={(e) => handleLinkShot(pId, e.target.value)}
                                    >
                                        <option value="">Empty</option>
                                        {existingShots.map(s => <option key={s.id} value={s.id}>#{s.shot_number}</option>)}
                                    </AppSelect>
                                 </div>
                                 {shot ? (
                                     <div className="text-center p-2"><span className="text-xs font-bold block mb-1" style={{ color: theme.colors.bluePrimary }}>Shot #{shot.shot_number}</span><span className="text-2xs line-clamp-3" style={{ color: 'var(--color-text-muted)' }}>{panelPrompts[pId]}</span></div>
                                 ) : (
                                     <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>EMPTY</span>
                                 )}
                             </div>
                         )
                     })}
                 </div>
             ) : (
                 <div className="w-full max-w-7xl flex gap-8 h-full animate-in fade-in duration-300">
                     <div className="flex-1 flex flex-col gap-4 min-w-0">
                         <div className="flex justify-between items-center"><h4 className="font-bold text-sm" style={{ color: 'var(--color-text-muted)' }}>原始宫格图</h4></div>
                         <div className="flex-1 rounded-2xl border overflow-hidden flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}><img src={gridImageUrl} className="max-w-full max-h-full object-contain shadow-2xl animate-in fade-in duration-300" /></div>
                     </div>
                     <div className="w-96 flex flex-col gap-4 pl-8 h-full" style={{ borderLeft: '1px solid var(--color-border-primary)' }}>
                         <div className="flex justify-between items-center">
                             <h4 className="font-bold text-sm" style={{ color: 'var(--color-text-muted)' }}>切片预览 ({splitImages.length})</h4>
                             <div className="flex gap-2">
                                {splitImages.length === 0 ? (
                                  <button onClick={handleSplitGrid} disabled={isSplitting} className="px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold shadow-lg transition-all hover:scale-105 disabled:opacity-50" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                                    {isSplitting ? <Loader2 className="animate-spin" size={12}/> : <LayoutGrid size={12}/>}
                                    {isSplitting ? "裁剪中..." : "裁剪宫格"}
                                  </button>
                                ) : (
                                  <>
                                    <button onClick={handleUpscaleAll} className="px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold transition-all hover:scale-105" style={{ backgroundColor: `${theme.colors.bluePrimary}20`, color: theme.colors.bluePrimary, border: `1px solid ${theme.colors.bluePrimary}40` }} title="AI 超分放大（会轻微修改内容）"><Wand2 size={12}/> AI 高清化</button>
                                    <button onClick={handleConfirmUpdate} className="px-3 py-1.5 rounded-lg flex items-center gap-1 text-xs font-bold shadow-lg" style={{ backgroundColor: theme.colors.greenPrimary, color: 'white' }}><CheckCircle2 size={12}/> 回填</button>
                                  </>
                                )}
                             </div>
                         </div>
                         {splitImages.length === 0 ? (
                           <div className="flex-1 flex items-center justify-center rounded-xl border border-dashed" style={{ borderColor: 'var(--color-border-primary)' }}>
                             <div className="text-center p-8">
                               <LayoutGrid size={32} className="mx-auto mb-3 opacity-30" style={{ color: 'var(--color-text-muted)' }}/>
                               <p className="text-sm font-bold mb-1" style={{ color: 'var(--color-text-secondary)' }}>点击"裁剪宫格"开始</p>
                               <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>裁剪后可选择 AI 高清化或直接回填</p>
                             </div>
                           </div>
                         ) : (
                         <div className="flex-1 overflow-y-auto custom-scrollbar grid grid-cols-2 gap-6 auto-rows-max pr-2 content-start pb-20">
                             {splitImages.map((img, idx) => {
                                 const shot = existingShots.find(s => s.id === panelSourceMap[img.panelId]);
                                 if (!shot) return null;
                                 return (
                                     <div key={idx} className="relative group rounded-lg overflow-hidden border w-full" style={{ aspectRatio: getCellAspectRatio(), backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
                                         <img src={img.base64} className={`w-full h-full object-cover transition-opacity ${img.loading ? 'opacity-50' : 'opacity-100'}`} />
                                         {img.isUpscaled && <div className="absolute top-1 right-1 text-2xs font-bold px-1 rounded" style={{ backgroundColor: 'var(--color-green-light)', color: 'var(--color-green-primary)' }}>HD</div>}
                                         {img.loading && <div className="absolute inset-0 flex items-center justify-center"><Loader2 className="animate-spin" size={16} style={{ color: 'var(--color-text-primary)' }}/></div>}
                                         <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center flex-col gap-2" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                                             <span className="text-xs font-bold" style={{ color: 'var(--color-text-primary)' }}>Shot {shot.shot_number}</span>
                                             {!img.loading && !img.isUpscaled && <button onClick={() => handleUpscaleSingle(img)} className="px-2 py-1 rounded flex items-center gap-1 text-2xs transition-all hover:scale-105" style={{ backgroundColor: theme.colors.bluePrimary, color: 'white' }} title="AI 超分（会轻微修改）"><Wand2 size={10}/> AI 高清化</button>}
                                             {img.isUpscaled && <span className="text-2xs font-bold px-2 py-1 rounded" style={{ backgroundColor: 'var(--color-green-light)', color: theme.colors.greenPrimary }}>已超分</span>}
                                         </div>
                                     </div>
                                 );
                             })}
                         </div>
                         )}
                     </div>
                 </div>
             )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default GridSequenceWizard;
